import { useState } from 'react'
import Link from 'next/link'
import { useSelector, useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import { Menu, X, Bus, MapPin, Calculator, User, LogOut, ChevronRight } from 'lucide-react'

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const auth = useSelector(state => state.auth)
  const user = auth?.user || null
  const dispatch = useDispatch()
  const router = useRouter()

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    dispatch({ type: 'LOGOUT' })
    router.push('/')
    setIsMenuOpen(false)
  }

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  const navLinks = [
    { href: '/search-results?vehicleType=bus', label: 'Buses', icon: Bus },
    { href: '/search-results?vehicleType=minibus', label: 'Minibuses', icon: Bus },
    { href: '/search-results?vehicleType=car', label: 'Cars', icon: Bus },
    { href: '/hotels', label: 'Hotels', icon: MapPin },
    { href: '/get-quote', label: 'Get a Quote', icon: Calculator },
  ]

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 group">
            <div className="bg-green-600 p-2 rounded-lg group-hover:bg-green-700 transition-colors">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-900 leading-none">Blisss<span className="text-green-600">Bus</span></span>
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-0.5">Part of EzyMove</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-all"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* User Actions / Mobile Toggle */}
          <div className="flex items-center gap-3">
            {/* Desktop User Section */}
            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <>
                  <Link href="/dashboard" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-green-700 transition-colors">My Dashboard</Link>
                  <div className="h-6 w-px bg-gray-200 mx-2"></div>
                  <span className="text-sm font-medium text-gray-700">Hi, {user.name}</span>
                  <button onClick={handleLogout} className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-all flex items-center gap-2">
                    <LogOut className="h-4 w-4" /> Logout
                  </button>
                </>
              ) : (
                <>
                  <Link href="/login" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-green-700 transition-colors">Sign In</Link>
                  <Link href="/register" className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white shadow-xl animate-in fade-in slide-in-from-top duration-300">
          <div className="px-4 pt-4 pb-6 space-y-4">
            {/* Nav Links */}
            <div className="grid grid-cols-1 gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50 text-gray-900 font-bold hover:bg-green-50 hover:text-green-700 transition-all border border-transparent hover:border-green-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-white p-2 rounded-lg shadow-sm">
                      <link.icon className="h-5 w-5 text-green-600" />
                    </div>
                    {link.label}
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </Link>
              ))}
            </div>

            <hr className="border-gray-100" />

            {/* User Section (Mobile) */}
            <div className="space-y-3">
              {user ? (
                <>
                  <div className="flex items-center gap-3 px-3 mb-2">
                    <div className="bg-green-100 p-2.5 rounded-full">
                      <User className="h-5 w-5 text-green-700" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Logged in as</p>
                      <p className="font-bold text-gray-900">{user.name}</p>
                    </div>
                  </div>
                  <Link
                    href="/dashboard"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 w-full p-3 rounded-xl bg-green-600 text-white font-bold shadow-lg shadow-green-600/20"
                  >
                    <Calculator className="h-5 w-5" /> My Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full p-3 rounded-xl bg-red-50 text-red-600 font-bold border border-red-100"
                  >
                    <LogOut className="h-5 w-5" /> Logout
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    href="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-center rounded-xl bg-gray-50 text-gray-900 font-bold border border-gray-200"
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/register"
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-center rounded-xl bg-green-600 text-white font-bold shadow-lg shadow-green-600/20"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}